"use client";

import type { ColumnDef } from "@tanstack/react-table";
import {
  ArrowUpDown,
  ArrowUpRight,
  Ban,
  Briefcase,
  Building,
  Building2,
  Check,
  CircleCheck,
  CircleDashed,
  CirclePause,
  CircleX,
  Clock,
  CreditCard,
  Globe,
  Info,
  LayoutDashboard,
  LogIn,
  MapPin,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  SearchX,
  Settings,
  Trash2,
  Users,
  Wrench,
  X,
  type LucideIcon
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode
} from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
  useParams
} from "react-router-dom";
import { toast } from "sonner";

import { useAppHeader, type HeaderCrumb } from "@/components/app-header";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ListCard,
  ListRow,
  formCardClassName as FORM_CARD_CLASSNAME
} from "@/components/ui/list-card";
import {
  ChipMenu,
  ListSearch,
  ListToolbar
} from "@/components/ui/list-toolbar";
import { PageHeader, SectionHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { VisaTag } from "../../../components/common/VisaTag";
import { useLawFirms } from "../../../features/lawFirms/context/LawFirmsContext";
import { LawFirmsAPI } from "../../../api/lawFirms";
import { LegacyDraftingCard } from "./LegacyDraftingCard";
import { LawyerAPI } from "../../../api/lawyerApi";
import { ImpersonationAPI } from "../../../api/impersonationApi";
import {
  FirmSize,
  ListLawFirmForeignNationalsResponse,
  LawFirm,
  LawyerDTO,
  LawyerRole,
  Source,
  Subscription,
  SubscriptionWithPlanDTO,
  SubscriptionPlanType,
  SubscriptionStatus,
  EnterpriseAuthProvider,
  EnterpriseProviderType
} from "../../../api/types/lawFirm";
import { ForeignNational } from "../../../api/types/foreignNational";
import { MattersAPI } from "../../../api/matters";
import { MatterDTO } from "../../../api/types/matter";
import { asVisaValue } from "../../../utils/matterDisplay";
import {
  caseWorkspacePath,
  matterCaseRef
} from "../../../features/caseIde/routes";
import { getWorkflowType, WorkflowType } from "../../../types/workflow.type";
import {
  SORT_OPTIONS,
  sortLabel,
  type SortFilter,
  initialsFromName
} from "../../ForeignNationals/clientsListFilters";

type LawFirmTab =
  | "overview"
  | "users"
  | "clients"
  | "children"
  | "subscription"
  | "settings";

const parseLawFirmTab = (value?: string | null): LawFirmTab => {
  const normalized = (value ?? "").trim();
  switch (normalized) {
    case "users":
    case "clients":
    case "children":
    case "subscription":
    case "settings":
    case "overview":
      return normalized as LawFirmTab;
    default:
      return "overview";
  }
};

const SIDEBAR_NAV_TABS: Array<{
  id: LawFirmTab;
  label: string;
  icon: LucideIcon;
}> = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: Users },
  { id: "clients", label: "Clients", icon: Briefcase },
  { id: "subscription", label: "Subscription", icon: CreditCard },
  { id: "children", label: "Subsidiaries", icon: Building },
  { id: "settings", label: "Settings", icon: Settings }
];

const getTabLabel = (tab: LawFirmTab): string => {
  return SIDEBAR_NAV_TABS.find((t) => t.id === tab)?.label ?? "Details";
};

const showToast = (title: string, description: string) => {
  toast.success(title, { description, duration: 5000 });
};

const showErrorToast = (title: string, description: string) => {
  toast.error(title, { description, duration: 6000 });
};

type StatusMeta = { tone: StatusTone; icon: LucideIcon; label: string };

const getSubscriptionStatusMeta = (status?: string | null): StatusMeta => {
  switch (status) {
    case SubscriptionStatus.ACTIVE:
      return { tone: "success", icon: CircleCheck, label: "Active" };
    case SubscriptionStatus.PAUSED:
      return { tone: "warn", icon: CirclePause, label: "Paused" };
    case SubscriptionStatus.CANCELLED:
      return { tone: "neutral", icon: CircleX, label: "Cancelled" };
    case SubscriptionStatus.EXPIRED:
      return { tone: "neutral", icon: Clock, label: "Expired" };
    default:
      return {
        tone: "neutral",
        icon: CircleDashed,
        label: String(status ?? "—")
      };
  }
};

function StatusMetaBadge({ meta }: { meta: StatusMeta }) {
  const Icon = meta.icon;
  return (
    <StatusBadge tone={meta.tone}>
      <Icon aria-hidden />
      {meta.label}
    </StatusBadge>
  );
}

const getLawyerRoleTone = (role: LawyerRole | string): StatusTone => {
  switch (role) {
    case LawyerRole.OWNER:
    case LawyerRole.ATTORNEY:
      return "success";
    case LawyerRole.ADMIN:
    case LawyerRole.PARALEGAL:
      return "accent";
    default:
      return "neutral";
  }
};

const US_STATE_ABBREVIATIONS = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY"
] as const;

const LAWYER_ROLE_FILTER_OPTIONS: LawyerRole[] = [
  LawyerRole.OWNER,
  LawyerRole.ADMIN,
  LawyerRole.ATTORNEY,
  LawyerRole.PARALEGAL
];

const LAWYER_ROLE_ACCESS_HELP: Record<
  | LawyerRole.OWNER
  | LawyerRole.ADMIN
  | LawyerRole.ATTORNEY
  | LawyerRole.PARALEGAL,
  string
> = {
  [LawyerRole.OWNER]:
    "Full access to firm settings, users, and subscriptions. Intended for firm owners.",
  [LawyerRole.ADMIN]:
    "Administrative access to manage firm users and operations. Limited billing/subscription access.",
  [LawyerRole.ATTORNEY]:
    "Standard casework access for managing client matters. No firm admin controls.",
  [LawyerRole.PARALEGAL]:
    "Support access for assisting on client matters. No firm admin controls."
};

const LAWYER_TITLE_OPTIONS = ["Mr.", "Ms.", "Mrs.", "Dr.", "Esq."] as const;

type ChoiceOption = { value: string; label: ReactNode };

const toChoiceOptions = (values: readonly string[]): ChoiceOption[] =>
  values.map((value) => ({ value, label: value }));

const FIRM_SIZE_OPTIONS = toChoiceOptions(Object.values(FirmSize));
const SOURCE_OPTIONS = toChoiceOptions(Object.values(Source));
const SUBSCRIPTION_PLAN_TYPE_OPTIONS = toChoiceOptions(
  Object.values(SubscriptionPlanType)
);
const US_STATE_OPTIONS = toChoiceOptions(US_STATE_ABBREVIATIONS);
const LAWYER_TITLE_CHOICES = toChoiceOptions(LAWYER_TITLE_OPTIONS);

const SUBSCRIPTION_STATUS_OPTIONS: ChoiceOption[] = [
  { value: SubscriptionStatus.ACTIVE, label: "Active" },
  { value: SubscriptionStatus.PAUSED, label: "Paused" },
  { value: SubscriptionStatus.CANCELLED, label: "Cancelled" },
  { value: SubscriptionStatus.EXPIRED, label: "Expired" }
];

const LAWYER_ROLE_OPTIONS: ChoiceOption[] = [
  { value: LawyerRole.OWNER, label: "Owner" },
  { value: LawyerRole.ADMIN, label: "Admin" },
  { value: LawyerRole.ATTORNEY, label: "Attorney" },
  { value: LawyerRole.PARALEGAL, label: "Paralegal" }
];

const ENTERPRISE_PROVIDER_TYPE_OPTIONS: ChoiceOption[] = [
  { value: "saml", label: "SAML" },
  { value: "oidc", label: "OIDC" }
];

const USER_STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" }
];

const SHEET_CONTENT_CLASSNAME =
  "gap-0 p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-130";
const LAW_FIRM_CLIENTS_PAGE_LIMIT = 50;
const TABLE_INFINITE_SCROLL_THRESHOLD_PX = 200;

const getClientFullName = (client: ForeignNational) =>
  [client.first_name, client.middle_name, client.last_name]
    .filter((part) => typeof part === "string" && part.trim())
    .join(" ");

const normalizeClient = (client: ForeignNational): ForeignNational => {
  const assignedRaw = (client as { assigned_to_user_ids?: unknown })
    .assigned_to_user_ids;
  const assigned =
    Array.isArray(assignedRaw) &&
    assignedRaw.every((value) => typeof value === "string")
      ? assignedRaw
      : Array.isArray(assignedRaw)
      ? assignedRaw.filter(
          (value): value is string => typeof value === "string"
        )
      : [];

  return {
    ...client,
    first_name: typeof client.first_name === "string" ? client.first_name : "",
    middle_name:
      typeof client.middle_name === "string" ? client.middle_name : "",
    last_name: typeof client.last_name === "string" ? client.last_name : "",
    email: typeof client.email === "string" ? client.email : "",
    practice_name:
      typeof client.practice_name === "string" ? client.practice_name : "",
    assigned_to_user_ids: assigned,
    country_of_birth:
      typeof client.country_of_birth === "string"
        ? client.country_of_birth
        : null,
    country_of_citizenship:
      typeof client.country_of_citizenship === "string"
        ? client.country_of_citizenship
        : null,
    current_immigration_status:
      typeof client.current_immigration_status === "string"
        ? client.current_immigration_status
        : null
  };
};

const getClientCountryLabel = (client: ForeignNational) => {
  const countryOfBirth = (client.country_of_birth ?? "").trim();
  if (countryOfBirth) return countryOfBirth;
  const countryOfCitizenship = (client.country_of_citizenship ?? "").trim();
  if (countryOfCitizenship) return countryOfCitizenship;
  return "—";
};

const getWorkflowItems = (matters?: MatterDTO[]) => {
  if (!Array.isArray(matters)) return [];

  const seen = new Set<string>();
  const items: Array<{
    visa: string;
    isRfe: boolean;
    workflowType: WorkflowType;
    matter: MatterDTO;
  }> = [];

  matters.forEach((matter) => {
    const visa = (matter.case?.visa ?? "").toString().trim();
    if (!visa) return;
    const isRfe = Boolean(
      (matter.individual as { is_rfe?: unknown; isRfe?: unknown })?.is_rfe ??
        (matter.individual as { is_rfe?: unknown; isRfe?: unknown })?.isRfe
    );
    const workflowType = getWorkflowType(
      (matter.individual as { workflow_type?: string | null })?.workflow_type,
      isRfe
    );
    const key = `${visa}::${workflowType}`;
    if (seen.has(key)) return;
    seen.add(key);
    items.push({ visa, isRfe, workflowType, matter });
  });

  return items;
};

const WorkflowMatterTag = ({
  visa,
  isRfe,
  workflowType,
  matter,
  size,
  onOpenMatter
}: {
  visa: string;
  isRfe?: boolean;
  workflowType?: WorkflowType | string | null;
  matter: MatterDTO | null;
  size: "xs" | "sm";
  onOpenMatter: (matter: MatterDTO) => void;
}) => {
  const isClickable = Boolean(matter);

  return (
    <button
      type="button"
      className={[
        "group inline-flex items-center",
        isClickable ? "cursor-pointer" : "cursor-default"
      ].join(" ")}
      onClick={(event) => {
        event.stopPropagation();
        if (matter) onOpenMatter(matter);
      }}
      disabled={!isClickable}
      aria-label={isClickable ? `Open ${visa} matter` : visa}
    >
      <VisaTag
        visaType={asVisaValue(visa)}
        isRfe={Boolean(isRfe)}
        workflowType={workflowType}
        size={size}
        rightIcon={isClickable ? "FeatherArrowUpRight" : undefined}
        rightIconVisibility="hover"
      />
    </button>
  );
};

type DetailSectionProps = {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
};

function DetailSection({
  title,
  description,
  actions,
  children
}: DetailSectionProps) {
  return (
    <section className="flex w-full min-w-0 flex-col gap-3">
      <SectionHeader
        title={title}
        description={description}
        actions={actions}
      />
      {children}
    </section>
  );
}

type FieldProps = {
  label: string;
  helpText?: ReactNode;
  className?: string;
};

function TextInput({
  label,
  helpText,
  className,
  ...props
}: ComponentProps<typeof Input> & FieldProps) {
  const id = useId();
  return (
    <div className={cn("flex min-w-0 flex-1 flex-col gap-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} {...props} />
      {helpText ? <p className="text-sm text-fg-3">{helpText}</p> : null}
    </div>
  );
}

function TextareaInput({
  label,
  helpText,
  className,
  ...props
}: ComponentProps<typeof Textarea> & FieldProps) {
  const id = useId();
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      <Textarea id={id} {...props} />
      {helpText ? <p className="text-sm text-fg-3">{helpText}</p> : null}
    </div>
  );
}

function ChoiceInput({
  label,
  helpText,
  className,
  placeholder,
  value,
  options,
  disabled,
  onValueChange
}: FieldProps & {
  placeholder: string;
  value: string | undefined;
  options: ChoiceOption[];
  disabled?: boolean;
  onValueChange: (value: string) => void;
}) {
  const id = useId();
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={value || undefined}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {helpText ? <p className="text-sm text-fg-3">{helpText}</p> : null}
    </div>
  );
}

function SwitchRow({
  title,
  checked,
  onCheckedChange
}: {
  title: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <ListRow
      title={title}
      control={
        <Switch
          aria-label={title}
          checked={checked}
          onCheckedChange={onCheckedChange}
        />
      }
    />
  );
}

function FormActions({ children }: { children: ReactNode }) {
  return <div className="flex items-center justify-end gap-2">{children}</div>;
}

export function LawFirmsDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { lawFirmId } = useParams<{ lawFirmId: string }>();
  const {
    selectedLawFirm,
    fetchLawFirm,
    updateLawFirm,
    createLawyer,
    createInvite,
    isLoadingDetail,
    isSaving
  } = useLawFirms();

  const hasMismatchedSelectedLawFirm = Boolean(
    selectedLawFirm && lawFirmId && selectedLawFirm.uid !== lawFirmId
  );
  const currentLawFirm =
    lawFirmId && selectedLawFirm?.uid === lawFirmId ? selectedLawFirm : null;
  const isInitialFirmLoading =
    hasMismatchedSelectedLawFirm || (isLoadingDetail && !currentLawFirm);

  const [name, setName] = useState("");
  const [parentLawFirmId, setParentLawFirmId] = useState("");
  const [creditBalance, setCreditBalance] = useState("");
  const [firmSize, setFirmSize] = useState<FirmSize | undefined>(undefined);
  const [source, setSource] = useState<Source | undefined>(undefined);
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [stripeCustomerId, setStripeCustomerId] = useState("");
  const [
    enableWhitelabelForeignNationalIntake,
    setEnableWhitelabelForeignNationalIntake
  ] = useState(false);

  // Enterprise SSO (Identity Platform)
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [enforceSso, setEnforceSso] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [allowedDomains, setAllowedDomains] = useState("");
  const [enterpriseProviders, setEnterpriseProviders] = useState<
    EnterpriseAuthProvider[]
  >([]);

  const [activeTab, setActiveTab] = useState<LawFirmTab>(() =>
    parseLawFirmTab(searchParams.get("tab"))
  );
  const [showCreateLawyer, setShowCreateLawyer] = useState(false);
  const [editingLawyerId, setEditingLawyerId] = useState<string | null>(null);
  const [isSavingEditLawyer, setIsSavingEditLawyer] = useState(false);
  const [togglingLawyerId, setTogglingLawyerId] = useState<string | null>(null);
  const [impersonationTarget, setImpersonationTarget] =
    useState<LawyerDTO | null>(null);
  const [impersonationReason, setImpersonationReason] = useState("");
  const [isStartingImpersonation, setIsStartingImpersonation] = useState(false);

  const isUserFormOpen = showCreateLawyer || Boolean(editingLawyerId);
  const isEditingLawyer = Boolean(editingLawyerId);
  const isImpersonationDrawerOpen =
    activeTab === "users" && Boolean(impersonationTarget);

  const closeImpersonationDrawer = useCallback(() => {
    setImpersonationTarget(null);
    setImpersonationReason("");
    setIsStartingImpersonation(false);
  }, []);

  const openImpersonationDrawer = useCallback((lawyer: LawyerDTO) => {
    setImpersonationTarget(lawyer);
    setImpersonationReason("");
  }, []);

  const handleStartImpersonation = useCallback(async () => {
    if (!impersonationTarget?.uid) return;
    const reason = impersonationReason.trim();
    if (reason.length < 5) {
      showErrorToast(
        "Reason required",
        "Please provide a short reason (at least 5 characters)."
      );
      return;
    }

    setIsStartingImpersonation(true);
    try {
      const resp = await ImpersonationAPI.start({
        targetLawyerId: impersonationTarget.uid,
        reason
      });
      if (!resp?.token) {
        throw new Error("Missing token");
      }
      showToast(
        "Impersonation started",
        `Logging in as ${impersonationTarget.email ?? "user"}...`
      );
      navigate("/login", {
        state: {
          customToken: resp.token,
          customRole: "lawyer",
          impersonationTenantId: resp.tenantId ?? null,
          impersonationId: resp.impersonationId
        }
      });
    } catch (e) {
      showErrorToast(
        "Failed to log in as user",
        "Please try again or contact support."
      );
    } finally {
      setIsStartingImpersonation(false);
      closeImpersonationDrawer();
    }
  }, [
    closeImpersonationDrawer,
    impersonationReason,
    impersonationTarget?.email,
    impersonationTarget?.uid,
    navigate
  ]);

  useEffect(() => {
    const nextTab = parseLawFirmTab(searchParams.get("tab"));
    setActiveTab((currentTab) =>
      currentTab === nextTab ? currentTab : nextTab
    );
  }, [searchParams]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = (params.get("tab") ?? "").trim();
    const current = (raw || "overview") as LawFirmTab;
    if (current === activeTab) return;
    params.set("tab", activeTab);
    const next = params.toString();
    navigate(
      { pathname: location.pathname, search: next ? `?${next}` : "" },
      { replace: true }
    );
  }, [activeTab, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (activeTab !== "users" && isUserFormOpen) {
      setShowCreateLawyer(false);
      setEditingLawyerId(null);
    }
  }, [activeTab, isUserFormOpen]);

  const [lawyers, setLawyers] = useState<LawyerDTO[]>([]);
  const [isLoadingLawyers, setIsLoadingLawyers] = useState(false);
  const [subscriptions, setSubscriptions] = useState<SubscriptionWithPlanDTO[]>(
    []
  );
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(false);
  const [childLawFirms, setChildLawFirms] = useState<LawFirm[]>([]);
  const [isLoadingChildLawFirms, setIsLoadingChildLawFirms] = useState(false);
  const [showCreateSubscription, setShowCreateSubscription] = useState(false);
  const [editingSubscriptionId, setEditingSubscriptionId] = useState<
    string | null
  >(null);
  const [isSavingSubscription, setIsSavingSubscription] = useState(false);
  const [subscriptionPlanId, setSubscriptionPlanId] = useState("");
  const [subscriptionPlanType, setSubscriptionPlanType] =
    useState<SubscriptionPlanType>(SubscriptionPlanType.FREE_TRIAL);
  const [subscriptionCreditAllowance, setSubscriptionCreditAllowance] =
    useState("");
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus>(SubscriptionStatus.ACTIVE);
  const [subscriptionStartDate, setSubscriptionStartDate] = useState("");
  const [subscriptionEndDate, setSubscriptionEndDate] = useState("");
  const [usersSearch, setUsersSearch] = useState("");
  const [usersRoleFilter, setUsersRoleFilter] = useState<"all" | string>("all");
  const [usersStatusFilter, setUsersStatusFilter] = useState<
    "all" | "active" | "disabled"
  >("all");
  const [clients, setClients] = useState<ForeignNational[]>([]);
  const [clientsTotalCount, setClientsTotalCount] = useState(0);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingMoreClients, setIsLoadingMoreClients] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [clientsOffset, setClientsOffset] = useState(0);
  const [clientsHasMore, setClientsHasMore] = useState(true);
  const [clientsSearchInput, setClientsSearchInput] = useState("");
  const [clientsSearch, setClientsSearchValue] = useState("");
  const [clientsRefreshNonce, setClientsRefreshNonce] = useState(0);
  const [clientsSortFilter, setClientsSortFilter] =
    useState<SortFilter>("newest");
  const [clientMattersById, setClientMattersById] = useState<
    Record<string, MatterDTO[] | undefined>
  >({});
  const clientSearchTimeoutRef = useRef<number | null>(null);
  const clientsRequestSeqRef = useRef(0);
  const clientsAppendNextRef = useRef(false);
  const clientsTableScrollContainerRef = useRef<HTMLDivElement | null>(null);
  const clientsInfiniteScrollInFlightRef = useRef(false);
  const clientMattersInFlightRef = useRef<Set<string>>(new Set());

  const isSubscriptionFormOpen =
    showCreateSubscription || Boolean(editingSubscriptionId);

  const [lawFirmsForParentSelect, setLawFirmsForParentSelect] = useState<
    LawFirm[]
  >([]);
  const [
    isLoadingLawFirmsForParentSelect,
    setIsLoadingLawFirmsForParentSelect
  ] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    const fetchAllLawFirms = async () => {
      setIsLoadingLawFirmsForParentSelect(true);
      const limit = 250;

      const fetchPages = async (
        pageToken?: string,
        acc: LawFirm[] = []
      ): Promise<LawFirm[]> => {
        const resp = await LawFirmsAPI.adminList({
          limit,
          pageToken,
          includeDeleted: false
        });
        const nextAcc = [...acc, ...(resp.lawFirms ?? [])];
        const nextToken = resp.nextPageToken
          ? String(resp.nextPageToken)
          : undefined;
        return nextToken ? fetchPages(nextToken, nextAcc) : nextAcc;
      };

      try {
        const all = await fetchPages(undefined, []);
        if (!isCancelled) setLawFirmsForParentSelect(all);
      } catch {
        if (!isCancelled) setLawFirmsForParentSelect([]);
      } finally {
        if (!isCancelled) setIsLoadingLawFirmsForParentSelect(false);
      }
    };

    if (activeTab === "settings") {
      fetchAllLawFirms();
    }

    return () => {
      isCancelled = true;
    };
  }, [activeTab]);

  const parentLawFirmOptions = useMemo(() => {
    const currentId = lawFirmId ?? currentLawFirm?.uid ?? "";
    return (lawFirmsForParentSelect ?? [])
      .filter((f) => {
        if (!f?.uid) return false;
        if (f.uid === currentId) return false;
        if (f.is_deleted || f.deleted_at) return false;
        return true;
      })
      .slice()
      .sort((a, b) =>
        String(a?.name ?? "").localeCompare(String(b?.name ?? ""), undefined, {
          sensitivity: "base"
        })
      );
  }, [currentLawFirm?.uid, lawFirmId, lawFirmsForParentSelect]);

  useEffect(() => {
    if (activeTab !== "subscription" && isSubscriptionFormOpen) {
      setShowCreateSubscription(false);
      setEditingSubscriptionId(null);
    }
  }, [activeTab, isSubscriptionFormOpen]);

  const visibleLawyers = useMemo(() => {
    const q = usersSearch.trim().toLowerCase();
    return lawyers.filter((l) => {
      if (q) {
        const name = `${l.firstName ?? ""} ${l.lastName ?? ""}`.toLowerCase();
        const email = (l.email ?? "").toLowerCase();
        if (!name.includes(q) && !email.includes(q)) return false;
      }

      if (usersRoleFilter !== "all") {
        const role = (l.role ?? l.lawyerRole ?? l.lawyer_role ?? "").toString();
        if (role !== usersRoleFilter) return false;
      }

      if (usersStatusFilter !== "all") {
        const isDisabled = Boolean(l.disabled);
        if (usersStatusFilter === "disabled" && !isDisabled) return false;
        if (usersStatusFilter === "active" && isDisabled) return false;
      }

      return true;
    });
  }, [lawyers, usersRoleFilter, usersSearch, usersStatusFilter]);

  const formatIsoDate = (value?: string | null) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "2-digit",
      year: "numeric"
    });
  };

  const toDateInputValue = (value?: string | null) => {
    if (!value) return "";
    // Prefer string slicing to avoid timezone shifts.
    const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
    if (match?.[1]) return match[1];
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };

  const toApiDateTimeValue = (value: string) => {
    const v = value.trim();
    if (!v) return null;
    // If user already provided an ISO datetime, keep it.
    if (v.includes("T")) return v;
    // Convert YYYY-MM-DD to a datetime string.
    return `${v}T00:00:00Z`;
  };

  const formatEpochMsDateTime = (value?: number | null) => {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit"
    });
  };

  const formatRoleLabel = (role: LawyerRole | string) => {
    return String(role)
      .replace(/[_-]+/g, " ")
      .split(" ")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  };

  useEffect(() => {
    return () => {
      if (clientSearchTimeoutRef.current) {
        window.clearTimeout(clientSearchTimeoutRef.current);
      }
    };
  }, []);

  const resetToFirstClientsPage = useCallback(() => {
    clientsAppendNextRef.current = false;
    clientsInfiniteScrollInFlightRef.current = false;
    setClientsOffset(0);
    setClientsHasMore(true);
    setClients([]);
    setClientMattersById({});
  }, []);

  const fetchFirmClients = useCallback(async () => {
    if (activeTab !== "clients" || !lawFirmId) return;

    clientsRequestSeqRef.current += 1;
    const seq = clientsRequestSeqRef.current;
    const shouldAppend = clientsAppendNextRef.current && clientsOffset > 0;
    clientsAppendNextRef.current = false;

    if (shouldAppend) setIsLoadingMoreClients(true);
    else setIsLoadingClients(true);
    setClientsError(null);

    try {
      const response: ListLawFirmForeignNationalsResponse =
        await LawFirmsAPI.listForeignNationals(lawFirmId, {
          limit: LAW_FIRM_CLIENTS_PAGE_LIMIT,
          offset: clientsOffset,
          q: clientsSearch.trim() ? clientsSearch.trim() : undefined,
          sort: clientsSortFilter
        });
      if (clientsRequestSeqRef.current !== seq) return;

      const page = (response.foreign_nationals ?? []).map(normalizeClient);
      setClients((prev) => (shouldAppend ? [...prev, ...page] : page));
      setClientsTotalCount(response.total_count ?? 0);
      if (page.length === 0) {
        setClientsHasMore(false);
      } else {
        const nextLoaded = clientsOffset + page.length;
        const total = response.total_count ?? 0;
        setClientsHasMore(
          total > 0
            ? nextLoaded < total
            : page.length >= LAW_FIRM_CLIENTS_PAGE_LIMIT
        );
      }
    } catch (error) {
      if (clientsRequestSeqRef.current !== seq) return;
      const message =
        error instanceof Error ? error.message : "Failed to load clients";
      setClientsError(message);
      if (!shouldAppend) {
        setClients([]);
        setClientsTotalCount(0);
      }
    } finally {
      if (clientsRequestSeqRef.current === seq) {
        if (shouldAppend) setIsLoadingMoreClients(false);
        else setIsLoadingClients(false);
        clientsInfiniteScrollInFlightRef.current = false;
      }
    }
  }, [activeTab, clientsOffset, clientsSearch, clientsSortFilter, lawFirmId]);

  useEffect(() => {
    fetchFirmClients();
  }, [clientsRefreshNonce, fetchFirmClients]);

  const refreshClients = useCallback(() => {
    resetToFirstClientsPage();
    setClientsRefreshNonce((value) => value + 1);
  }, [resetToFirstClientsPage]);

  const setClientsSearch = useCallback(
    (value: string) => {
      setClientsSearchInput(value);
      resetToFirstClientsPage();
      if (clientSearchTimeoutRef.current) {
        window.clearTimeout(clientSearchTimeoutRef.current);
      }
      clientSearchTimeoutRef.current = window.setTimeout(() => {
        setClientsSearchValue(value);
      }, 300);
    },
    [resetToFirstClientsPage]
  );

  const handleClientsSortChange = useCallback(
    (value: SortFilter) => {
      setClientsSortFilter(value);
      resetToFirstClientsPage();
    },
    [resetToFirstClientsPage]
  );

  const loadMoreClients = useCallback(() => {
    if (clientsError) return;
    if (!clientsHasMore) return;
    if (isLoadingMoreClients) return;
    if (clientsInfiniteScrollInFlightRef.current) return;
    clientsInfiniteScrollInFlightRef.current = true;
    clientsAppendNextRef.current = true;
    setClientsOffset(clients.length);
  }, [clients.length, clientsError, clientsHasMore, isLoadingMoreClients]);

  const maybeLoadMoreClients = useCallback(() => {
    const element = clientsTableScrollContainerRef.current;
    if (!element) return;
    if (clientsError) return;
    if (!clientsHasMore) return;
    if (isLoadingClients || isLoadingMoreClients) return;
    if (clientsInfiniteScrollInFlightRef.current) return;

    const distanceFromBottom =
      element.scrollHeight - (element.scrollTop + element.clientHeight);
    if (distanceFromBottom > TABLE_INFINITE_SCROLL_THRESHOLD_PX) return;
    loadMoreClients();
  }, [
    clientsError,
    clientsHasMore,
    isLoadingClients,
    isLoadingMoreClients,
    loadMoreClients
  ]);

  useEffect(() => {
    if (activeTab !== "clients") return;
    if (isLoadingClients) return;
    maybeLoadMoreClients();
  }, [activeTab, clients.length, isLoadingClients, maybeLoadMoreClients]);

  useEffect(() => {
    if (activeTab !== "clients") return;
    if (isLoadingClients || isLoadingMoreClients) return;
    if (clientsError) return;

    const idsToFetch = clients
      .map((client) => (client.uid ?? "").trim())
      .filter((uid) => {
        if (!uid) return false;
        if (clientMattersById[uid] !== undefined) return false;
        if (clientMattersInFlightRef.current.has(uid)) return false;
        return true;
      });

    if (idsToFetch.length === 0) return;

    idsToFetch.forEach((uid) => clientMattersInFlightRef.current.add(uid));

    const fetchOne = async (uid: string) => {
      try {
        const response = await MattersAPI.listForForeignNational(uid);
        setClientMattersById((prev) => ({
          ...prev,
          [uid]: Array.isArray(response) ? response : []
        }));
      } catch {
        setClientMattersById((prev) => ({ ...prev, [uid]: [] }));
      } finally {
        clientMattersInFlightRef.current.delete(uid);
      }
    };

    const run = async () => {
      const concurrency = 4;
      for (let i = 0; i < idsToFetch.length; i += concurrency) {
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(idsToFetch.slice(i, i + concurrency).map(fetchOne));
      }
    };

    run();
  }, [
    activeTab,
    clientMattersById,
    clients,
    clientsError,
    isLoadingClients,
    isLoadingMoreClients
  ]);

  const matchingClientsLabel =
    clientsTotalCount > 0
      ? `Showing ${clients.length} of ${clientsTotalCount} clients`
      : "Showing 0 of 0 clients";

  const isClientsEmpty =
    !clientsSearchInput.trim() && clientsTotalCount === 0 && !isLoadingClients;

  useEffect(() => {
    let isCancelled = false;
    if (activeTab === "users" && lawFirmId) {
      setIsLoadingLawyers(true);
      LawFirmsAPI.listLawyers(lawFirmId)
        .then((result) => {
          if (isCancelled) return;
          setLawyers(Array.isArray(result) ? result : []);
        })
        .finally(() => {
          if (isCancelled) return;
          setIsLoadingLawyers(false);
        });
    }

    return () => {
      isCancelled = true;
    };
  }, [activeTab, lawFirmId]);

  useEffect(() => {
    let isCancelled = false;
    if (activeTab === "subscription" && lawFirmId) {
      setIsLoadingSubscriptions(true);
      LawFirmsAPI.listSubscriptions(lawFirmId)
        .then((result) => {
          if (isCancelled) return;
          setSubscriptions(Array.isArray(result) ? result : []);
        })
        .finally(() => {
          if (isCancelled) return;
          setIsLoadingSubscriptions(false);
        });
    }

    return () => {
      isCancelled = true;
    };
  }, [activeTab, lawFirmId]);

  useEffect(() => {
    let isCancelled = false;
    if (activeTab === "children" && lawFirmId) {
      setIsLoadingChildLawFirms(true);
      LawFirmsAPI.listChildren(lawFirmId)
        .then((result) => {
          if (isCancelled) return;
          setChildLawFirms(Array.isArray(result) ? result : []);
        })
        .finally(() => {
          if (isCancelled) return;
          setIsLoadingChildLawFirms(false);
        });
    }

    return () => {
      isCancelled = true;
    };
  }, [activeTab, lawFirmId]);

  const [lawyerEmail, setLawyerEmail] = useState("");
  const [lawyerFirstName, setLawyerFirstName] = useState("");
  const [lawyerLastName, setLawyerLastName] = useState("");
  const [lawyerMiddleName, setLawyerMiddleName] = useState("");
  const [lawyerRole, setLawyerRole] = useState<LawyerRole>(LawyerRole.ATTORNEY);
  const [lawyerTitle, setLawyerTitle] = useState("");
  const [lawyerPhone, setLawyerPhone] = useState("");
  const [lawyerAddress, setLawyerAddress] = useState("");
  const [lawyerCity, setLawyerCity] = useState("");
  const [lawyerState, setLawyerState] = useState("");
  const [lawyerZip, setLawyerZip] = useState("");

  const resetLawyerForm = () => {
    setLawyerEmail("");
    setLawyerFirstName("");
    setLawyerLastName("");
    setLawyerMiddleName("");
    setLawyerRole(LawyerRole.ATTORNEY);
    setLawyerTitle("");
    setLawyerPhone("");
    setLawyerAddress("");
    setLawyerCity("");
    setLawyerState("");
    setLawyerZip("");
  };

  const resetSubscriptionForm = () => {
    setSubscriptionPlanId("");
    setSubscriptionPlanType(SubscriptionPlanType.FREE_TRIAL);
    setSubscriptionCreditAllowance("");
    setSubscriptionStatus(SubscriptionStatus.ACTIVE);
    setSubscriptionStartDate("");
    setSubscriptionEndDate("");
  };

  const closeSubscriptionForm = () => {
    setShowCreateSubscription(false);
    setEditingSubscriptionId(null);
    resetSubscriptionForm();
  };

  const openCreateSubscription = () => {
    setEditingSubscriptionId(null);
    resetSubscriptionForm();
    setShowCreateSubscription(true);
  };

  const openEditSubscription = (s: Subscription) => {
    setShowCreateSubscription(false);
    setEditingSubscriptionId(s.uid);
    setSubscriptionPlanId(s.plan_id ?? "");
    setSubscriptionStatus(s.subscription_status ?? SubscriptionStatus.ACTIVE);
    setSubscriptionStartDate(toDateInputValue(s.start_date));
    setSubscriptionEndDate(toDateInputValue(s.end_date));
  };

  const closeUserForm = () => {
    setShowCreateLawyer(false);
    setEditingLawyerId(null);
    resetLawyerForm();
  };

  const openCreateLawyer = () => {
    setEditingLawyerId(null);
    resetLawyerForm();
    setShowCreateLawyer(true);
  };

  const openEditLawyer = (u: LawyerDTO) => {
    setShowCreateLawyer(false);
    setEditingLawyerId(u.uid);
    setLawyerEmail(u.email ?? "");
    setLawyerFirstName(u.firstName ?? "");
    setLawyerLastName(u.lastName ?? "");
    setLawyerMiddleName(u.middleName ?? "");
    const role = (u.role ?? u.lawyerRole ?? u.lawyer_role ?? "")?.toString();
    const nextRole =
      role === LawyerRole.OWNER ||
      role === LawyerRole.ADMIN ||
      role === LawyerRole.ATTORNEY ||
      role === LawyerRole.PARALEGAL
        ? (role as LawyerRole)
        : LawyerRole.ATTORNEY;
    setLawyerRole(nextRole);
    setLawyerTitle(u.title ?? "");
    setLawyerPhone(u.phone ?? "");
    setLawyerAddress(u.address ?? "");
    setLawyerCity(u.city ?? "");
    setLawyerState(u.state ?? "");
    setLawyerZip(u.zipcode ?? "");
  };

  useEffect(() => {
    if (lawFirmId) {
      fetchLawFirm(lawFirmId);
    }
  }, [fetchLawFirm, lawFirmId]);

  useEffect(() => {
    if (!currentLawFirm) return;
    setName(currentLawFirm.name ?? "");
    setParentLawFirmId(currentLawFirm.parent_law_firm_id ?? "");
    setFirmSize(currentLawFirm.firm_size ?? undefined);
    setSource(currentLawFirm.source ?? undefined);
    setAddress(currentLawFirm.address ?? "");
    setCity(currentLawFirm.city ?? "");
    setState(currentLawFirm.state ?? "");
    setZipcode(currentLawFirm.zipcode ?? "");
    setPhone(currentLawFirm.phone ?? "");
    setWebsite(currentLawFirm.website ?? "");
    setStripeCustomerId(currentLawFirm.stripe_customer_id ?? "");
    setEnableWhitelabelForeignNationalIntake(
      Boolean(currentLawFirm.enable_whitelabel_foreign_national_intake)
    );
    setCreditBalance(
      currentLawFirm.credit_balance !== null &&
        currentLawFirm.credit_balance !== undefined
        ? String(currentLawFirm.credit_balance)
        : ""
    );

    setSsoEnabled(Boolean(currentLawFirm.sso_enabled));
    setEnforceSso(Boolean(currentLawFirm.enforce_sso));
    setTenantId(currentLawFirm.identity_platform_tenant_id ?? "");
    setAllowedDomains(
      Array.isArray(currentLawFirm.allowed_email_domains)
        ? currentLawFirm.allowed_email_domains.join("\n")
        : ""
    );
    setEnterpriseProviders(
      Array.isArray(currentLawFirm.enterprise_providers)
        ? currentLawFirm.enterprise_providers.map((p) => ({
            provider_id: p.provider_id ?? "",
            type: (p.type as EnterpriseProviderType) ?? "saml",
            label: p.label ?? "",
            enabled: p.enabled ?? true
          }))
        : []
    );
  }, [currentLawFirm]);

  const initials = useMemo(() => {
    if (!currentLawFirm?.name) return "L";
    return currentLawFirm.name.slice(0, 1).toUpperCase();
  }, [currentLawFirm?.name]);
  const logoUrl = currentLawFirm?.logo_url ?? undefined;

  const handleSave = async () => {
    if (!lawFirmId) return;
    const normalizedDomains = allowedDomains
      .split(/\r?\n|,/g)
      .map((d) => d.trim().toLowerCase())
      .map((d) => (d.startsWith("@") ? d.slice(1) : d))
      .filter(Boolean);
    const uniqueDomains = Array.from(new Set(normalizedDomains));

    const providersPayload = (enterpriseProviders ?? [])
      .map((p) => ({
        provider_id: String(p.provider_id ?? "").trim(),
        type: String(p.type ?? "")
          .trim()
          .toLowerCase(),
        label: String(p.label ?? "").trim(),
        enabled: Boolean(p.enabled ?? true)
      }))
      .filter((p) => p.provider_id.length > 0 && p.type.length > 0);

    await updateLawFirm(lawFirmId, {
      name: name.trim() || null,
      parent_law_firm_id: parentLawFirmId.trim() || null,
      firm_size: firmSize ?? null,
      source: source ?? null,
      address: address.trim() || null,
      city: city.trim() || null,
      state: state.trim() || null,
      zipcode: zipcode.trim() || null,
      phone: phone.trim() || null,
      website: website.trim() || null,
      stripe_customer_id: stripeCustomerId.trim() || null,
      credit_balance: creditBalance ? Number(creditBalance) : null,
      enable_whitelabel_foreign_national_intake:
        enableWhitelabelForeignNationalIntake,

      sso_enabled: ssoEnabled,
      enforce_sso: enforceSso,
      identity_platform_tenant_id: tenantId.trim() || null,
      allowed_email_domains: uniqueDomains,
      enterprise_providers: providersPayload
    });
  };

  const addEnterpriseProvider = () => {
    setEnterpriseProviders((prev) => [
      ...(prev ?? []),
      { provider_id: "", type: "saml", label: "", enabled: true }
    ]);
  };

  const updateEnterpriseProvider = (
    index: number,
    patch: Partial<EnterpriseAuthProvider>
  ) => {
    setEnterpriseProviders((prev) =>
      (prev ?? []).map((p, i) => (i === index ? { ...p, ...patch } : p))
    );
  };

  const removeEnterpriseProvider = (index: number) => {
    setEnterpriseProviders((prev) =>
      (prev ?? []).filter((_, i) => i !== index)
    );
  };

  const canCreateLawyer =
    lawyerEmail.trim().length > 0 &&
    lawyerFirstName.trim().length > 0 &&
    lawyerLastName.trim().length > 0;

  const lawyerRoleHelp = useMemo(() => {
    switch (lawyerRole) {
      case LawyerRole.OWNER:
        return LAWYER_ROLE_ACCESS_HELP[LawyerRole.OWNER];
      case LawyerRole.ADMIN:
        return LAWYER_ROLE_ACCESS_HELP[LawyerRole.ADMIN];
      case LawyerRole.ATTORNEY:
        return LAWYER_ROLE_ACCESS_HELP[LawyerRole.ATTORNEY];
      case LawyerRole.PARALEGAL:
        return LAWYER_ROLE_ACCESS_HELP[LawyerRole.PARALEGAL];
      default:
        return "—";
    }
  }, [lawyerRole]);

  const handleCreateLawyer = async () => {
    if (!lawFirmId) return;
    if (!canCreateLawyer) return;
    if (currentLawFirm?.sso_enabled) {
      await createInvite(lawFirmId, {
        email: lawyerEmail.trim(),
        firstName: lawyerFirstName.trim(),
        lastName: lawyerLastName.trim(),
        role: lawyerRole,
        title: lawyerTitle.trim() || undefined
      });
    } else {
      await createLawyer(lawFirmId, {
        email: lawyerEmail.trim(),
        randomizePassword: true,
        firstName: lawyerFirstName.trim(),
        lastName: lawyerLastName.trim(),
        middleName: lawyerMiddleName.trim() || undefined,
        role: lawyerRole,
        title: lawyerTitle.trim() || undefined,
        address: lawyerAddress.trim() || undefined,
        city: lawyerCity.trim() || undefined,
        state: lawyerState.trim() || undefined,
        zipcode: lawyerZip.trim() || undefined,
        phone: lawyerPhone.trim() || undefined
      });
      try {
        setIsLoadingLawyers(true);
        const refreshed = await LawFirmsAPI.listLawyers(lawFirmId);
        setLawyers(Array.isArray(refreshed) ? refreshed : []);
      } finally {
        setIsLoadingLawyers(false);
      }
    }
    resetLawyerForm();
    closeUserForm();
  };

  const handleUpdateLawyer = async () => {
    if (!editingLawyerId) return;
    if (!lawFirmId) return;
    setIsSavingEditLawyer(true);
    try {
      await LawyerAPI.update(editingLawyerId, {
        firstName: lawyerFirstName.trim() || undefined,
        middleName: lawyerMiddleName.trim() || undefined,
        lastName: lawyerLastName.trim() || undefined,
        role: lawyerRole,
        title: lawyerTitle.trim() || undefined,
        address: lawyerAddress.trim() || undefined,
        city: lawyerCity.trim() || undefined,
        state: lawyerState.trim() || undefined,
        zipcode: lawyerZip.trim() || undefined,
        phone: lawyerPhone.trim() || undefined
      });
      const refreshed = await LawFirmsAPI.listLawyers(lawFirmId);
      setLawyers(Array.isArray(refreshed) ? refreshed : []);
      showToast(
        "User updated",
        "The user details and access settings were saved successfully."
      );
      closeUserForm();
    } finally {
      setIsSavingEditLawyer(false);
    }
  };

  const handleSetLawyerDisabled = async (
    lawyerId: string,
    nextDisabled: boolean
  ) => {
    if (!lawFirmId) return;
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      nextDisabled
        ? "Disable this user? They will no longer be able to sign in."
        : "Re-enable this user? They will be able to sign in again."
    );
    if (!confirmed) return;

    setTogglingLawyerId(lawyerId);
    try {
      if (nextDisabled) {
        await LawyerAPI.disable(lawyerId);
        showToast(
          "User disabled",
          "The user can no longer sign in to this law firm."
        );
      } else {
        await LawyerAPI.enable(lawyerId);
        showToast(
          "User enabled",
          "The user can sign in to this law firm again."
        );
      }
      const refreshed = await LawFirmsAPI.listLawyers(lawFirmId);
      setLawyers(Array.isArray(refreshed) ? refreshed : []);
      if (editingLawyerId === lawyerId) {
        closeUserForm();
      }
    } finally {
      setTogglingLawyerId(null);
    }
  };

  const canCreateSubscription = (() => {
    const parsed = Number(subscriptionCreditAllowance);
    return Number.isFinite(parsed);
  })();

  const refreshLawyers = async () => {
    if (!lawFirmId) return;
    setIsLoadingLawyers(true);
    try {
      const refreshed = await LawFirmsAPI.listLawyers(lawFirmId);
      setLawyers(Array.isArray(refreshed) ? refreshed : []);
    } finally {
      setIsLoadingLawyers(false);
    }
  };

  const refreshChildLawFirms = async () => {
    if (!lawFirmId) return;
    setIsLoadingChildLawFirms(true);
    try {
      const refreshed = await LawFirmsAPI.listChildren(lawFirmId);
      setChildLawFirms(Array.isArray(refreshed) ? refreshed : []);
    } finally {
      setIsLoadingChildLawFirms(false);
    }
  };

  const refreshSubscriptions = async () => {
    if (!lawFirmId) return;
    setIsLoadingSubscriptions(true);
    try {
      const refreshed = await LawFirmsAPI.listSubscriptions(lawFirmId);
      setSubscriptions(Array.isArray(refreshed) ? refreshed : []);
    } finally {
      setIsLoadingSubscriptions(false);
    }
  };

  const handleCreateSubscription = async () => {
    if (!lawFirmId) return;
    if (!canCreateSubscription) return;
    setIsSavingSubscription(true);
    try {
      await LawFirmsAPI.createSubscription(lawFirmId, {
        plan: {
          subscription_plan_type: subscriptionPlanType,
          credit_allowance: (() => {
            const parsed = Number(subscriptionCreditAllowance);
            return Number.isFinite(parsed) ? parsed : undefined;
          })()
        },
        subscription_status: subscriptionStatus,
        start_date: toApiDateTimeValue(subscriptionStartDate),
        end_date: toApiDateTimeValue(subscriptionEndDate)
      });
      await refreshSubscriptions();
      await fetchLawFirm(lawFirmId);
      closeSubscriptionForm();
      showToast(
        "Subscription created",
        "The new subscription has been added to this law firm."
      );
    } finally {
      setIsSavingSubscription(false);
    }
  };

  const handleUpdateSubscription = async () => {
    if (!editingSubscriptionId) return;
    setIsSavingSubscription(true);
    try {
      await LawFirmsAPI.updateSubscription(editingSubscriptionId, {
        subscription_status: subscriptionStatus,
        start_date: toApiDateTimeValue(subscriptionStartDate),
        end_date: toApiDateTimeValue(subscriptionEndDate)
      });
      await refreshSubscriptions();
      closeSubscriptionForm();
      showToast(
        "Subscription updated",
        "The subscription changes were saved successfully."
      );
    } finally {
      setIsSavingSubscription(false);
    }
  };

  const firmName =
    (currentLawFirm?.name ?? "Law Firm").toString().trim() || "Law Firm";
  const firmLocation =
    currentLawFirm?.city || currentLawFirm?.state
      ? `${currentLawFirm?.city ?? ""}${
          currentLawFirm?.city && currentLawFirm?.state ? ", " : ""
        }${currentLawFirm?.state ?? ""}`.trim()
      : "—";
  const firmPhone = (currentLawFirm?.phone ?? "").toString().trim() || "—";
  const firmWebsite = (currentLawFirm?.website ?? "").toString().trim() || "—";

  const currentSubscriptionPlanType =
    currentLawFirm?.subscription_plan?.subscription_plan_type ??
    currentLawFirm?.subscription_plan_type ??
    null;
  const effectiveSubscriptionStatus =
    currentLawFirm?.subscription?.subscription_status ??
    currentLawFirm?.subscription_status ??
    null;

  const statusBadge = useMemo((): StatusMeta => {
    if (currentLawFirm?.is_deleted) {
      return { tone: "neutral", icon: CircleX, label: "Deleted" };
    }
    const s = (effectiveSubscriptionStatus ?? "").toString().trim();
    if (!s) {
      return { tone: "neutral", icon: CircleDashed, label: "—" };
    }
    return getSubscriptionStatusMeta(s);
  }, [currentLawFirm?.is_deleted, effectiveSubscriptionStatus]);

  const breadcrumbSegments = useMemo((): HeaderCrumb[] => {
    const isOverview = activeTab === "overview";
    const segments: HeaderCrumb[] = [
      { id: "internal-tools", label: "Internal Tools", icon: Wrench },
      {
        id: "law-firms",
        label: "Law Firms",
        to: "/admin/law-firms",
        icon: Building2
      },
      {
        id: "law-firm",
        label: firmName,
        current: isOverview,
        to: !isOverview ? `${location.pathname}?tab=overview` : undefined,
        avatar: { image: logoUrl, initials }
      }
    ];

    if (!isOverview) {
      segments.push({
        id: "tab",
        label: getTabLabel(activeTab),
        current: true
      });
    }
    return segments;
  }, [activeTab, firmName, initials, location.pathname, logoUrl]);

  useAppHeader(
    useMemo(() => ({ crumbs: breadcrumbSegments }), [breadcrumbSegments])
  );

  const saveActions = (
    <FormActions>
      <Button
        variant="secondary"
        disabled={!lawFirmId || isSaving}
        onClick={handleSave}
      >
        {isSaving ? <Spinner /> : <Save />}
        Save
      </Button>
    </FormActions>
  );

  const overviewTab = isInitialFirmLoading ? (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-fg-3">
      <Spinner />
      Loading firm details...
    </div>
  ) : (
    <>
      <DetailSection title="Subscription">
        <ListCard>
          <ListRow
            title="Plan"
            control={
              <span className="text-base text-fg-2">
                {currentSubscriptionPlanType
                  ? String(currentSubscriptionPlanType)
                  : "—"}
              </span>
            }
          />
          <ListRow
            title="Credit Balance"
            control={
              <span className="text-base text-fg-2 tabular-nums">
                {typeof currentLawFirm?.credit_balance === "number"
                  ? currentLawFirm.credit_balance
                  : "—"}
              </span>
            }
          />
        </ListCard>
      </DetailSection>

      <DetailSection title="Firm Information">
        <div className={FORM_CARD_CLASSNAME}>
          <div className="flex gap-3 max-sm:flex-col">
            <TextInput
              label="Law Firm Name *"
              placeholder="Enter firm name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <ChoiceInput
              className="w-56 max-sm:w-full"
              label="Firm Size"
              placeholder="Select firm size"
              value={firmSize}
              options={FIRM_SIZE_OPTIONS}
              onValueChange={(value) => setFirmSize(value as FirmSize)}
            />
          </div>
          <div className="flex gap-3 max-sm:flex-col">
            <TextInput
              label="Website"
              placeholder="https://firmwebsite.com"
              value={website}
              onChange={(event) => setWebsite(event.target.value)}
            />
            <TextInput
              label="Phone"
              placeholder="+1 (555) 000-0000"
              value={phone}
              onChange={(event) => setPhone(event.target.value)}
            />
          </div>
          <TextInput
            label="Address"
            placeholder="Street address"
            value={address}
            onChange={(event) => setAddress(event.target.value)}
          />
          <div className="flex gap-3 max-sm:flex-col">
            <TextInput
              label="City"
              placeholder="City"
              value={city}
              onChange={(event) => setCity(event.target.value)}
            />
            <ChoiceInput
              className="w-28 max-sm:w-full"
              label="State"
              placeholder="State"
              value={state}
              options={US_STATE_OPTIONS}
              onValueChange={setState}
            />
            <TextInput
              label="ZIP Code"
              placeholder="94105"
              value={zipcode}
              onChange={(event) => setZipcode(event.target.value)}
            />
          </div>
        </div>
      </DetailSection>

      <DetailSection title="Additional Details">
        <div className={FORM_CARD_CLASSNAME}>
          <ChoiceInput
            label="Source"
            placeholder="Select source"
            value={source}
            options={SOURCE_OPTIONS}
            onValueChange={(value) => setSource(value as Source)}
          />
        </div>
      </DetailSection>

      {saveActions}
    </>
  );

  const userColumns: ColumnDef<LawyerDTO, unknown>[] = [
    {
      id: "user",
      header: "User",
      cell: ({ row }) => {
        const u = row.original;
        return (
          <div className="flex min-w-0 items-center gap-3">
            <Avatar size="md">
              <AvatarFallback>
                {u.firstName?.slice(0, 1)?.toUpperCase() ?? "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-base text-fg">
                {`${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || "—"}
              </span>
              <span className="truncate text-sm text-fg-3">{u.email}</span>
            </div>
          </div>
        );
      }
    },
    {
      id: "role",
      header: "Role",
      cell: ({ row }) => {
        const u = row.original;
        const role = (u.role ?? u.lawyerRole ?? u.lawyer_role ?? "—") as
          | LawyerRole
          | string;
        return (
          <StatusBadge tone={getLawyerRoleTone(role)}>
            {role === "—" ? "—" : formatRoleLabel(role)}
          </StatusBadge>
        );
      }
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) =>
        row.original.disabled ? (
          <StatusBadge tone="neutral">
            <CircleX aria-hidden />
            Disabled
          </StatusBadge>
        ) : (
          <StatusBadge tone="success">
            <Check aria-hidden />
            Active
          </StatusBadge>
        )
    },
    {
      id: "created",
      header: "Created",
      cell: ({ row }) => (
        <span className="text-base whitespace-nowrap text-fg-2">
          {formatIsoDate(row.original.created_at)}
        </span>
      )
    },
    {
      id: "lastLogin",
      header: "Last Login",
      cell: ({ row }) => (
        <span className="text-base whitespace-nowrap text-fg-2">
          {formatEpochMsDateTime(
            row.original.lastSignedInAt ??
              row.original.last_signed_in_at ??
              null
          )}
        </span>
      )
    },
    {
      id: "actions",
      header: "",
      meta: { className: "w-12 text-right" },
      cell: ({ row }) => {
        const u = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="User actions"
                onClick={(event) => event.stopPropagation()}
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => openEditLawyer(u)}>
                <Pencil />
                Edit User
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => openImpersonationDrawer(u)}>
                <LogIn />
                Log In as User
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant={u.disabled ? "default" : "destructive"}
                disabled={togglingLawyerId === u.uid}
                onSelect={() => handleSetLawyerDisabled(u.uid, !u.disabled)}
              >
                {u.disabled ? <RotateCcw /> : <Ban />}
                {u.disabled ? "Enable User" : "Disable User"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }
    }
  ];

  const usersTab = (
    <DetailSection
      title="Users"
      actions={
        <>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Refresh"
            disabled={!lawFirmId || isLoadingLawyers}
            onClick={refreshLawyers}
          >
            <RefreshCw />
          </Button>
          {isUserFormOpen ? (
            <Button variant="secondary" onClick={closeUserForm}>
              <X />
              Close
            </Button>
          ) : (
            <Button variant="primary" onClick={openCreateLawyer}>
              <Plus />
              Add User
            </Button>
          )}
        </>
      }
    >
      <ListToolbar>
        <ListSearch
          aria-label="Search users"
          placeholder="Search users..."
          value={usersSearch}
          onValueChange={setUsersSearch}
        />
        <ChipMenu
          label="Role"
          anyLabel="All"
          options={LAWYER_ROLE_FILTER_OPTIONS.map((role) => ({
            value: role,
            label: formatRoleLabel(role)
          }))}
          value={usersRoleFilter === "all" ? "" : usersRoleFilter}
          onValueChange={(value) => setUsersRoleFilter(value || "all")}
        />
        <ChipMenu
          label="Status"
          anyLabel="All"
          options={USER_STATUS_FILTER_OPTIONS}
          value={usersStatusFilter === "all" ? "" : usersStatusFilter}
          onValueChange={(value) =>
            setUsersStatusFilter(
              value === "active" || value === "disabled" ? value : "all"
            )
          }
        />
      </ListToolbar>
      <DataTable
        variant="card"
        rowHeight="double"
        data={visibleLawyers}
        columns={userColumns}
        getRowId={(u) => u.uid}
        isLoading={isLoadingLawyers || isInitialFirmLoading}
        empty={<EmptyState icon={SearchX} title="No Matching Users Found" />}
      />
    </DetailSection>
  );

  const openClient = (clientId: string) => navigate(`/clients/${clientId}`);

  const clientColumns: ColumnDef<ForeignNational, unknown>[] = [
    {
      id: "name",
      header: "Client Name",
      cell: ({ row }) => {
        const client = row.original;
        const fullName = getClientFullName(client) || client.email || "Client";
        return (
          <div className="flex min-w-0 items-center gap-3">
            <Avatar size="md">
              <AvatarFallback>{initialsFromName(fullName)}</AvatarFallback>
            </Avatar>
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-base text-fg">{fullName}</span>
              <span className="truncate text-sm text-fg-3">
                {client.email ?? "—"}
              </span>
            </div>
          </div>
        );
      }
    },
    {
      id: "country",
      header: "Country of Origin",
      cell: ({ row }) => (
        <span className="text-base text-fg-2">
          {getClientCountryLabel(row.original)}
        </span>
      )
    },
    {
      id: "workflow",
      header: "Workflow",
      cell: ({ row }) => {
        const clientId = (row.original.uid ?? "").trim();
        const matters = clientMattersById[clientId];
        if (matters === undefined) {
          return <Skeleton className="h-5 w-20 rounded-full" />;
        }
        const workflowItems = getWorkflowItems(matters);
        const visibleWorkflows = workflowItems.slice(0, 2);
        const overflowCount = Math.max(
          0,
          workflowItems.length - visibleWorkflows.length
        );
        if (visibleWorkflows.length === 0) {
          return <span className="text-base text-fg-3">—</span>;
        }
        return (
          <div className="flex flex-wrap items-center gap-2">
            {visibleWorkflows.map((workflow) => (
              <WorkflowMatterTag
                key={`${clientId}-${workflow.visa}-${workflow.workflowType}`}
                visa={workflow.visa}
                isRfe={workflow.isRfe}
                workflowType={workflow.workflowType}
                matter={workflow.matter ?? null}
                size="sm"
                onOpenMatter={(matter) =>
                  navigate(caseWorkspacePath(matterCaseRef(matter)))
                }
              />
            ))}
            {overflowCount > 0 ? (
              <StatusBadge tone="neutral">{`+${overflowCount}`}</StatusBadge>
            ) : null}
          </div>
        );
      }
    },
    {
      id: "created",
      header: "Created",
      cell: ({ row }) => (
        <span className="text-base whitespace-nowrap text-fg-2">
          {formatIsoDate(row.original.created_at)}
        </span>
      )
    },
    {
      id: "actions",
      header: "",
      meta: { className: "w-12 text-right" },
      cell: ({ row }) => {
        const client = row.original;
        const clientId = (client.uid ?? "").trim();
        const fullName = getClientFullName(client) || client.email || "Client";
        return (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={`Open ${fullName}`}
            onClick={(event) => {
              event.stopPropagation();
              openClient(clientId);
            }}
          >
            <ArrowUpRight />
          </Button>
        );
      }
    }
  ];

  const clearClientsSearch = () => {
    setClientsSearchInput("");
    setClientsSearchValue("");
    refreshClients();
  };

  const clientsTab = (
    <DetailSection
      title="Clients"
      description={matchingClientsLabel}
      actions={
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Refresh clients"
          disabled={!lawFirmId || isLoadingClients}
          onClick={refreshClients}
        >
          <RefreshCw />
        </Button>
      }
    >
      <ListToolbar>
        <ListSearch
          aria-label="Search clients"
          placeholder="Search clients..."
          value={clientsSearchInput}
          onValueChange={setClientsSearch}
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" aria-label="Sort clients">
              <ArrowUpDown />
              <span>Sort: {sortLabel(clientsSortFilter)}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            <DropdownMenuLabel>Sort By</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={clientsSortFilter}
              onValueChange={(value) => {
                const option = SORT_OPTIONS.find((o) => o.value === value);
                if (option) handleClientsSortChange(option.value);
              }}
            >
              {SORT_OPTIONS.map(({ value, label }) => (
                <DropdownMenuRadioItem key={value} value={value}>
                  {label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </ListToolbar>
      {clientsError ? (
        <ListCard>
          <EmptyState
            icon={Briefcase}
            title="Couldn't Load Clients"
            description="Please try again."
            action={
              <Button variant="secondary" onClick={refreshClients}>
                Retry
              </Button>
            }
          />
        </ListCard>
      ) : (
        <DataTable
          variant="card"
          rowHeight="double"
          data={clients}
          columns={clientColumns}
          getRowId={(client, index) => client.uid || `client-${index}`}
          onRowClick={(client) => openClient((client.uid ?? "").trim())}
          isLoading={isLoadingClients}
          empty={
            isClientsEmpty ? (
              <EmptyState icon={Briefcase} title="No Clients Yet" />
            ) : (
              <EmptyState
                icon={SearchX}
                title="No Matching Clients Found"
                description="Try adjusting your search or sort."
                action={
                  <Button variant="secondary" onClick={clearClientsSearch}>
                    Clear Search
                  </Button>
                }
              />
            )
          }
        />
      )}
      {isLoadingMoreClients ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : null}
    </DetailSection>
  );

  const childLawFirmColumns: ColumnDef<LawFirm, unknown>[] = [
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="flex min-w-0 items-center gap-3">
          <Avatar size="md" shape="square">
            <AvatarFallback>
              {String(row.original.name ?? "L")
                .slice(0, 1)
                .toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="truncate text-base text-fg">
            {row.original.name ?? "—"}
          </span>
        </div>
      )
    },
    {
      id: "location",
      header: "Location",
      cell: ({ row }) => {
        const firm = row.original;
        const firmLocationLabel =
          firm.city || firm.state
            ? `${firm.city ?? ""}${firm.city && firm.state ? ", " : ""}${
                firm.state ?? ""
              }`.trim()
            : "—";
        return (
          <span className="text-base whitespace-nowrap text-fg-2">
            {firmLocationLabel}
          </span>
        );
      }
    },
    {
      id: "users",
      header: "Users",
      cell: ({ row }) => (
        <span className="text-base whitespace-nowrap text-fg-2 tabular-nums">
          {typeof row.original.lawyers_count === "number"
            ? row.original.lawyers_count
            : "—"}
        </span>
      )
    },
    {
      id: "actions",
      header: "",
      meta: { className: "w-12 text-right" },
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Open firm"
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/admin/law-firms/${row.original.uid}`);
          }}
        >
          <ArrowUpRight />
        </Button>
      )
    }
  ];

  const childrenTab = (
    <DetailSection
      title="Subsidiaries"
      actions={
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Refresh"
          disabled={!lawFirmId || isLoadingChildLawFirms}
          onClick={refreshChildLawFirms}
        >
          <RefreshCw />
        </Button>
      }
    >
      <DataTable
        variant="card"
        data={childLawFirms}
        columns={childLawFirmColumns}
        getRowId={(firm) => firm.uid}
        onRowClick={(firm) => navigate(`/admin/law-firms/${firm.uid}`)}
        isLoading={isLoadingChildLawFirms}
        empty={<EmptyState icon={Building2} title="No Subsidiaries Found" />}
      />
    </DetailSection>
  );

  const subscriptionColumns: ColumnDef<SubscriptionWithPlanDTO, unknown>[] = [
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <StatusMetaBadge
          meta={getSubscriptionStatusMeta(
            row.original.subscription.subscription_status
          )}
        />
      )
    },
    {
      id: "planType",
      header: "Plan Type",
      cell: ({ row }) => {
        const plan = row.original.subscription_plan ?? null;
        return (
          <span className="text-base text-fg-2">
            {plan ? String(plan.subscription_plan_type ?? "—") : "—"}
          </span>
        );
      }
    },
    {
      id: "creditAllowance",
      header: "Credit Allowance",
      cell: ({ row }) => {
        const allowance = row.original.subscription_plan?.credit_allowance;
        return (
          <span className="text-base text-fg-2 tabular-nums">
            {typeof allowance === "number" ? String(allowance) : "—"}
          </span>
        );
      }
    },
    {
      id: "start",
      header: "Start",
      cell: ({ row }) => (
        <span className="text-base whitespace-nowrap text-fg-2">
          {formatIsoDate(row.original.subscription.start_date)}
        </span>
      )
    },
    {
      id: "end",
      header: "End",
      cell: ({ row }) => (
        <span className="text-base whitespace-nowrap text-fg-2">
          {formatIsoDate(row.original.subscription.end_date)}
        </span>
      )
    },
    {
      id: "actions",
      header: "",
      meta: { className: "w-12 text-right" },
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Subscription actions"
              onClick={(event) => event.stopPropagation()}
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => openEditSubscription(row.original.subscription)}
            >
              <Pencil />
              Edit Subscription
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
  ];

  const subscriptionTab = (
    <DetailSection
      title="Subscriptions"
      actions={
        <>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Refresh"
            disabled={!lawFirmId || isLoadingSubscriptions}
            onClick={refreshSubscriptions}
          >
            <RefreshCw />
          </Button>
          {isSubscriptionFormOpen ? (
            <Button variant="secondary" onClick={closeSubscriptionForm}>
              <X />
              Close
            </Button>
          ) : (
            <Button variant="primary" onClick={openCreateSubscription}>
              <Plus />
              New Subscription
            </Button>
          )}
        </>
      }
    >
      <DataTable
        variant="card"
        data={subscriptions}
        columns={subscriptionColumns}
        getRowId={(row) => row.subscription.uid}
        isLoading={isLoadingSubscriptions || isInitialFirmLoading}
        empty={<EmptyState icon={CreditCard} title="No Subscriptions Found" />}
      />
    </DetailSection>
  );

  const settingsTab = (
    <>
      <DetailSection title="Credit Override">
        <div className={FORM_CARD_CLASSNAME}>
          <TextInput
            className="max-w-60"
            label="Credit Balance"
            placeholder="0"
            value={creditBalance}
            onChange={(event) => setCreditBalance(event.target.value)}
          />
        </div>
      </DetailSection>

      <DetailSection title="Stripe">
        <div className={FORM_CARD_CLASSNAME}>
          <TextInput
            label="Stripe Customer ID"
            placeholder="cus_123456789"
            value={stripeCustomerId}
            onChange={(event) => setStripeCustomerId(event.target.value)}
          />
        </div>
      </DetailSection>

      <DetailSection title="Add-ons">
        <ListCard>
          <SwitchRow
            title="Use Firm Logo on Foreign National Intake"
            checked={enableWhitelabelForeignNationalIntake}
            onCheckedChange={setEnableWhitelabelForeignNationalIntake}
          />
        </ListCard>
      </DetailSection>

      {currentLawFirm ? (
        <DetailSection title="Drafting">
          <LegacyDraftingCard lawFirm={currentLawFirm} />
        </DetailSection>
      ) : null}

      <DetailSection title="Firm Hierarchy">
        <div className={FORM_CARD_CLASSNAME}>
          <ChoiceInput
            className="w-1/2 max-sm:w-full"
            label="Parent Law Firm"
            placeholder="No parent"
            value={parentLawFirmId}
            disabled={
              !lawFirmId || isSaving || isLoadingLawFirmsForParentSelect
            }
            options={[
              { value: "__none__", label: "No Parent" },
              ...parentLawFirmOptions.map((firm) => ({
                value: firm.uid,
                label: firm.name
              }))
            ]}
            onValueChange={(value) =>
              setParentLawFirmId(value === "__none__" ? "" : value)
            }
          />
        </div>
      </DetailSection>

      <DetailSection title="Enterprise SSO">
        <ListCard>
          <SwitchRow
            title="Enable SSO"
            checked={ssoEnabled}
            onCheckedChange={setSsoEnabled}
          />
          {ssoEnabled ? (
            <>
              <SwitchRow
                title="Require SSO"
                checked={enforceSso}
                onCheckedChange={setEnforceSso}
              />
              <div className="flex flex-col gap-4 p-4">
                <TextInput
                  label="Tenant ID"
                  helpText="Identity Platform tenant id (e.g. tenant-abc123)."
                  placeholder="tenant-id"
                  value={tenantId}
                  onChange={(event) => setTenantId(event.target.value)}
                />
                <TextareaInput
                  label="Allowed Email Domain"
                  helpText="The domain that should use SSO for this firm (e.g. firm.com). Used to resolve the firm before login."
                  placeholder="firm.com"
                  value={allowedDomains}
                  onChange={(event) => setAllowedDomains(event.target.value)}
                />
              </div>
            </>
          ) : null}
        </ListCard>
      </DetailSection>

      {ssoEnabled ? (
        <DetailSection
          title="Enterprise Providers"
          actions={
            <Button variant="secondary" onClick={addEnterpriseProvider}>
              <Plus />
              Add Provider
            </Button>
          }
        >
          <ListCard>
            {enterpriseProviders.length === 0 ? (
              <p className="p-4 text-sm text-fg-3">No providers configured.</p>
            ) : (
              enterpriseProviders.map((p, idx) => (
                <div
                  key={`${p.provider_id}-${idx}`}
                  className="flex flex-col gap-3 p-4"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-base font-medium text-fg">
                      Provider {idx + 1}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Remove provider ${idx + 1}`}
                      onClick={() => removeEnterpriseProvider(idx)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                  <div className="flex gap-3 max-sm:flex-col">
                    <TextInput
                      label="Provider ID"
                      placeholder="saml.acme"
                      value={p.provider_id ?? ""}
                      onChange={(event) =>
                        updateEnterpriseProvider(idx, {
                          provider_id: event.target.value
                        })
                      }
                    />
                    <ChoiceInput
                      className="w-40 max-sm:w-full"
                      label="Type"
                      placeholder="Select type"
                      value={p.type as string}
                      options={ENTERPRISE_PROVIDER_TYPE_OPTIONS}
                      onValueChange={(value) =>
                        updateEnterpriseProvider(idx, {
                          type: value as EnterpriseProviderType
                        })
                      }
                    />
                  </div>
                  <div className="flex items-end gap-3 max-sm:flex-col max-sm:items-stretch">
                    <TextInput
                      label="Label"
                      placeholder="Okta"
                      value={p.label ?? ""}
                      onChange={(event) =>
                        updateEnterpriseProvider(idx, {
                          label: event.target.value
                        })
                      }
                    />
                    <div className="flex h-7 w-40 items-center gap-2 max-sm:w-full">
                      <Switch
                        id={`enterprise-provider-${idx}-enabled`}
                        checked={Boolean(p.enabled ?? true)}
                        onCheckedChange={(checked) =>
                          updateEnterpriseProvider(idx, { enabled: checked })
                        }
                      />
                      <Label htmlFor={`enterprise-provider-${idx}-enabled`}>
                        Enabled
                      </Label>
                    </div>
                  </div>
                </div>
              ))
            )}
          </ListCard>
        </DetailSection>
      ) : null}

      {saveActions}
    </>
  );

  const impersonationTargetName =
    `${impersonationTarget?.firstName ?? ""} ${
      impersonationTarget?.lastName ?? ""
    }`.trim() ||
    impersonationTarget?.email ||
    "—";

  return (
    <div
      ref={clientsTableScrollContainerRef}
      onScroll={activeTab === "clients" ? maybeLoadMoreClients : undefined}
      className="h-full w-full overflow-y-auto bg-surface"
    >
      <div className="mx-auto flex w-full max-w-280 flex-col gap-8 px-12 pt-10 pb-24 max-md:px-4">
        <div className="flex flex-col gap-3">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>Admin</BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/admin/law-firms">Law Firms</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  {isInitialFirmLoading ? "—" : firmName}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="flex min-w-0 items-start gap-3">
            {isInitialFirmLoading ? (
              <Skeleton className="size-10 shrink-0 rounded-lg" />
            ) : (
              <Avatar size="lg" shape="square">
                {logoUrl ? <AvatarImage src={logoUrl} alt="" /> : null}
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            )}
            <PageHeader
              className="min-w-0 flex-1"
              title={
                isInitialFirmLoading ? (
                  <Skeleton className="h-5 w-48" />
                ) : (
                  firmName
                )
              }
              description={
                <span className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <MapPin className="size-3.5 shrink-0" aria-hidden />
                    <span className="wrap-anywhere">
                      {isInitialFirmLoading ? "—" : firmLocation}
                    </span>
                  </span>
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <Phone className="size-3.5 shrink-0" aria-hidden />
                    <span className="wrap-anywhere">
                      {isInitialFirmLoading ? "—" : firmPhone}
                    </span>
                  </span>
                  <span className="inline-flex min-w-0 items-center gap-1">
                    <Globe className="size-3.5 shrink-0" aria-hidden />
                    <span className="wrap-anywhere">
                      {isInitialFirmLoading ? "—" : firmWebsite}
                    </span>
                  </span>
                </span>
              }
              actions={
                isInitialFirmLoading ? (
                  <Skeleton className="h-5 w-16 rounded-full" />
                ) : (
                  <StatusMetaBadge meta={statusBadge} />
                )
              }
            />
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(parseLawFirmTab(value))}
          className="gap-8"
        >
          <TabsList
            aria-label="Law firm sections"
            className="max-w-full overflow-x-auto"
          >
            {SIDEBAR_NAV_TABS.map(({ id, label, icon: Icon }) => (
              <TabsTrigger key={id} value={id}>
                <Icon aria-hidden />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="flex flex-col gap-8">
            {overviewTab}
          </TabsContent>
          <TabsContent value="users">{usersTab}</TabsContent>
          <TabsContent value="clients">{clientsTab}</TabsContent>
          <TabsContent value="subscription">{subscriptionTab}</TabsContent>
          <TabsContent value="children">{childrenTab}</TabsContent>
          <TabsContent value="settings" className="flex flex-col gap-8">
            {settingsTab}
          </TabsContent>
        </Tabs>
      </div>

      <Sheet
        open={activeTab === "subscription" && isSubscriptionFormOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) closeSubscriptionForm();
        }}
      >
        <SheetContent side="right" className={SHEET_CONTENT_CLASSNAME}>
          <SheetHeader className="border-b border-line-1">
            <SheetTitle>
              {editingSubscriptionId ? "Edit Subscription" : "New Subscription"}
            </SheetTitle>
            <SheetDescription>
              {editingSubscriptionId
                ? "Update subscription dates and status."
                : "Create a new subscription for this firm."}
            </SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
            <ChoiceInput
              className="w-56 max-sm:w-full"
              label="Status"
              placeholder="Select status"
              value={subscriptionStatus}
              options={SUBSCRIPTION_STATUS_OPTIONS}
              onValueChange={(value) =>
                setSubscriptionStatus(value as SubscriptionStatus)
              }
            />
            {editingSubscriptionId ? (
              <TextInput
                className="flex-none"
                label="Plan ID"
                placeholder="plan_..."
                value={subscriptionPlanId}
                disabled={true}
              />
            ) : (
              <>
                <ChoiceInput
                  className="w-56 max-sm:w-full"
                  label="Plan Type"
                  placeholder="Select plan type"
                  value={subscriptionPlanType}
                  options={SUBSCRIPTION_PLAN_TYPE_OPTIONS}
                  onValueChange={(value) =>
                    setSubscriptionPlanType(value as SubscriptionPlanType)
                  }
                />
                <TextInput
                  className="flex-none"
                  label="Credit Allowance"
                  placeholder="0"
                  value={subscriptionCreditAllowance}
                  onChange={(event) =>
                    setSubscriptionCreditAllowance(event.target.value)
                  }
                />
              </>
            )}
            <TextInput
              className="flex-none"
              label="Start Date"
              type="date"
              value={subscriptionStartDate}
              onChange={(event) => setSubscriptionStartDate(event.target.value)}
            />
            <TextInput
              className="flex-none"
              label="End Date"
              type="date"
              value={subscriptionEndDate}
              onChange={(event) => setSubscriptionEndDate(event.target.value)}
            />
            <p className="text-sm text-fg-3">
              {editingSubscriptionId
                ? "Plan type and credit allowance cannot be edited. Create a new subscription instead."
                : "Billing starts immediately when a subscription is created."}
            </p>
          </div>
          <SheetFooter className="flex-row justify-end border-t border-line-1">
            <Button variant="secondary" onClick={closeSubscriptionForm}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={
                editingSubscriptionId
                  ? isSavingSubscription || !lawFirmId
                  : isSavingSubscription || !lawFirmId || !canCreateSubscription
              }
              onClick={
                editingSubscriptionId
                  ? handleUpdateSubscription
                  : handleCreateSubscription
              }
            >
              {isSavingSubscription ? (
                <Spinner />
              ) : editingSubscriptionId ? (
                <Save />
              ) : (
                <Plus />
              )}
              {editingSubscriptionId ? "Save Changes" : "Create Subscription"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet
        open={activeTab === "users" && isUserFormOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) closeUserForm();
        }}
      >
        <SheetContent side="right" className={SHEET_CONTENT_CLASSNAME}>
          <SheetHeader className="border-b border-line-1">
            <SheetTitle>
              {isEditingLawyer ? "Edit User" : "Add User"}
            </SheetTitle>
            <SheetDescription>
              {isEditingLawyer
                ? "Update user details and access."
                : "Create a new user for this firm."}
            </SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
            <div className="flex flex-col gap-2">
              <ChoiceInput
                className="w-56 max-sm:w-full"
                label="Role"
                placeholder="Select role"
                value={lawyerRole}
                options={LAWYER_ROLE_OPTIONS}
                onValueChange={(value) => setLawyerRole(value as LawyerRole)}
              />
              <div className="flex items-start gap-2 text-sm text-fg-3">
                <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                <span>{lawyerRoleHelp}</span>
              </div>
            </div>
            <TextInput
              className="flex-none"
              label="Email"
              placeholder="lawyer@firm.com"
              value={lawyerEmail}
              disabled={isEditingLawyer}
              onChange={(event) => setLawyerEmail(event.target.value)}
            />
            <div className="flex gap-3 max-sm:flex-col">
              <TextInput
                label="First Name"
                placeholder="Alan"
                value={lawyerFirstName}
                onChange={(event) => setLawyerFirstName(event.target.value)}
              />
              <TextInput
                label="Middle Name"
                placeholder="Mathison"
                value={lawyerMiddleName}
                onChange={(event) => setLawyerMiddleName(event.target.value)}
              />
            </div>
            <TextInput
              className="flex-none"
              label="Last Name"
              placeholder="Turing"
              value={lawyerLastName}
              onChange={(event) => setLawyerLastName(event.target.value)}
            />
            <ChoiceInput
              className="w-56 max-sm:w-full"
              label="Title"
              placeholder="Select title"
              value={lawyerTitle}
              options={LAWYER_TITLE_CHOICES}
              onValueChange={setLawyerTitle}
            />
            <TextInput
              className="flex-none"
              label="Phone"
              placeholder="+1 (555) 000-0000"
              value={lawyerPhone}
              onChange={(event) => setLawyerPhone(event.target.value)}
            />
            <TextInput
              className="flex-none"
              label="Address"
              placeholder="123 Main St."
              value={lawyerAddress}
              onChange={(event) => setLawyerAddress(event.target.value)}
            />
            <div className="flex gap-3 max-sm:flex-col">
              <TextInput
                label="City"
                placeholder="San Francisco"
                value={lawyerCity}
                onChange={(event) => setLawyerCity(event.target.value)}
              />
              <ChoiceInput
                className="w-28 max-sm:w-full"
                label="State"
                placeholder="State"
                value={lawyerState}
                options={US_STATE_OPTIONS}
                onValueChange={setLawyerState}
              />
              <TextInput
                label="ZIP Code"
                placeholder="94105"
                value={lawyerZip}
                onChange={(event) => setLawyerZip(event.target.value)}
              />
            </div>
            <p className="text-sm text-fg-3">
              {currentLawFirm?.sso_enabled
                ? "This firm is SSO-enabled; an invite is created and the user activates by signing in with SSO."
                : "Passwords are generated automatically by the system."}
            </p>
          </div>
          <SheetFooter className="flex-row justify-end border-t border-line-1">
            <Button variant="secondary" onClick={closeUserForm}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={
                isEditingLawyer
                  ? isSavingEditLawyer || !editingLawyerId
                  : !lawFirmId || !canCreateLawyer || isSaving
              }
              onClick={
                isEditingLawyer ? handleUpdateLawyer : handleCreateLawyer
              }
            >
              {(isEditingLawyer ? isSavingEditLawyer : isSaving) ? (
                <Spinner />
              ) : isEditingLawyer ? (
                <Save />
              ) : (
                <Plus />
              )}
              {isEditingLawyer
                ? "Save Changes"
                : currentLawFirm?.sso_enabled
                ? "Invite"
                : "Create"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet
        open={isImpersonationDrawerOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) closeImpersonationDrawer();
        }}
      >
        <SheetContent side="right" className={SHEET_CONTENT_CLASSNAME}>
          <SheetHeader className="border-b border-line-1">
            <SheetTitle>Impersonate User</SheetTitle>
            <SheetDescription>This action is audited.</SheetDescription>
          </SheetHeader>
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
            <div className="flex flex-col gap-1.5">
              <span className="text-sm text-fg-2">Target User</span>
              <div className="flex min-w-0 items-baseline gap-2">
                <span className="min-w-0 truncate text-base font-medium text-fg">
                  {impersonationTargetName}
                </span>
                {impersonationTarget?.email ? (
                  <span className="min-w-0 truncate text-sm text-fg-3">
                    {impersonationTarget.email}
                  </span>
                ) : null}
              </div>
            </div>
            <TextareaInput
              label="Reason"
              helpText="Provide a justification for impersonating this user. This will be audited."
              placeholder="e.g. Investigating user report of drafting issue."
              value={impersonationReason}
              onChange={(event) => setImpersonationReason(event.target.value)}
            />
          </div>
          <SheetFooter className="flex-row justify-end border-t border-line-1">
            <Button
              variant="secondary"
              onClick={closeImpersonationDrawer}
              disabled={isStartingImpersonation}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={
                isStartingImpersonation ||
                !impersonationTarget?.uid ||
                impersonationReason.trim().length < 5
              }
              onClick={handleStartImpersonation}
            >
              {isStartingImpersonation ? <Spinner /> : <LogIn />}
              Log In as User
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
